import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  ChevronDown,
  Clock,
  Info,
  Loader2,
  Play,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import ProductPreviewShell from "./ProductPreviewShell";
import {
  DEBATE_ROLES,
  DEBATE_ROUNDS,
  FINAL_CONSENSUS,
  COMPARISON_DASHBOARD,
} from "./debateRoundsData";
import { PreviewPanelActions, SectionCta } from "./PreviewUi";
import { useLandingPreviewEditor } from "./LandingPreviewEditorProvider";
import { finalConsensusEditorPayload } from "./previewEditorContent";
import StreamingStructuredBody from "./StreamingStructuredBody";
import StreamCursor from "./StreamCursor";
import { useBulletListStream, useStructuredStream } from "./useMockStream";

const DEBATE_QUERY =
  "Does late delivery constitute a material breach when partial cure was tendered within the contractual cure window?";

function roleLogo(role) {
  if (role.logo) return role.logo;
  return `https://cdn.simpleicons.org/${role.logoCdn}/${role.accent.replace("#", "")}`;
}

function StaticArgCard({ role, arg }) {
  return (
    <div className="nl-debate-arg-card">
      <div className="nl-debate-arg-strip" style={{ background: role.accent }} />
      <div className="nl-debate-arg-inner">
        <div className="nl-debate-arg-meta">
          <span className="nl-debate-arg-model" style={{ color: role.accent }}>
            {role.model}
          </span>
          <span className="nl-debate-arg-role">{role.title}</span>
        </div>
        <h5 className="nl-debate-arg-heading">{arg.heading}</h5>
        <ul className="nl-debate-arg-bullets">
          {arg.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StreamingArgCard({ role, arg, streamKey, cardIndex, onComplete }) {
  const tokens = useMemo(
    () => [
      { type: "h5", text: arg.heading },
      ...arg.bullets.map((text) => ({ type: "li", text })),
    ],
    [arg],
  );
  const { tokens: streamed, isStreaming, isDone } = useStructuredStream(
    tokens,
    streamKey,
    { enabled: true, startDelay: cardIndex * 280, charDelay: 9 },
  );
  const finishedRef = useRef(false);

  useEffect(() => {
    finishedRef.current = false;
  }, [streamKey]);

  useEffect(() => {
    if (isDone && !finishedRef.current) {
      finishedRef.current = true;
      onComplete();
    }
  }, [isDone, onComplete]);

  return (
    <div className="nl-debate-arg-card">
      <div className="nl-debate-arg-strip" style={{ background: role.accent }} />
      <div className="nl-debate-arg-inner">
        <div className="nl-debate-arg-meta">
          <span className="nl-debate-arg-model" style={{ color: role.accent }}>
            {role.model}
          </span>
          <span className="nl-debate-arg-role">{role.title}</span>
        </div>
        <StreamingStructuredBody tokens={streamed} isStreaming={isStreaming} />
      </div>
    </div>
  );
}

function DebateRoundBlock({
  round,
  runGeneration,
  contentMode,
  isOpen,
  onToggle,
  onRoundComplete,
}) {
  const [cardsDone, setCardsDone] = useState(0);
  const completedRef = useRef(false);
  const streamKey = `${runGeneration}-${round.round}`;

  useEffect(() => {
    setCardsDone(0);
    completedRef.current = false;
  }, [streamKey, contentMode]);

  useEffect(() => {
    if (
      contentMode === "streaming" &&
      cardsDone >= DEBATE_ROLES.length &&
      !completedRef.current
    ) {
      completedRef.current = true;
      onRoundComplete();
    }
  }, [cardsDone, contentMode, onRoundComplete]);

  const onCardComplete = useCallback(() => {
    setCardsDone((c) => c + 1);
  }, []);

  return (
    <div className={`nl-debate-round ${isOpen ? "is-open" : ""}`}>
      <button
        type="button"
        className="nl-debate-round-head"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="nl-debate-round-num">
          <span className="nl-debate-round-pill">Round {round.round}/3</span>
          {round.title}
          {contentMode === "streaming" && (
            <span className="nl-debate-round-status">
              <Loader2 size={11} className="nl-spin" /> Streaming…
            </span>
          )}
        </span>
        <ChevronDown
          size={18}
          className={`nl-debate-chev ${isOpen ? "is-open" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="nl-debate-round-body">
          {contentMode === "pending" ? (
            <p className="nl-debate-round-pending">
              Waiting for Round {round.round - 1} to finish…
            </p>
          ) : (
            <div className="nl-debate-round-grid">
              {DEBATE_ROLES.map((role, index) => {
                const arg = round.arguments[role.key];
                if (contentMode === "streaming") {
                  return (
                    <StreamingArgCard
                      key={role.key}
                      role={role}
                      arg={arg}
                      streamKey={streamKey}
                      cardIndex={index}
                      onComplete={onCardComplete}
                    />
                  );
                }
                return (
                  <StaticArgCard key={role.key} role={role} arg={arg} />
                );
              })}
            </div>
          )}
          {contentMode !== "pending" && round.showContinues && (
            <p
              style={{
                padding: "9px 20px",
                fontSize: 11,
                fontWeight: 600,
                color: "var(--nl-brand)",
                opacity: 0.7,
                borderTop: "1px solid var(--nl-border)",
                margin: 0,
              }}
            >
              Full opening arguments continue →
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function FinalConsensusBlock({
  runGeneration,
  contentMode,
  isOpen,
  onToggle,
  onComplete,
  contentRef,
}) {
  const streamKey = `${runGeneration}-final`;
  const { bullets, isStreaming, isDone } = useBulletListStream(
    FINAL_CONSENSUS.bullets,
    streamKey,
    { enabled: contentMode === "streaming", charDelay: 9 },
  );
  const doneRef = useRef(false);

  useEffect(() => {
    doneRef.current = false;
  }, [streamKey, contentMode]);

  useEffect(() => {
    if (contentMode === "streaming" && isDone && !doneRef.current) {
      doneRef.current = true;
      onComplete();
    }
  }, [contentMode, isDone, onComplete]);

  const displayBullets =
    contentMode === "streaming" ? bullets : FINAL_CONSENSUS.bullets;

  return (
    <div className={`nl-debate-round nl-debate-round-final ${isOpen ? "is-open" : ""}`}>
      <button
        type="button"
        className="nl-debate-round-head"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="nl-debate-round-num">
          <span className="nl-debate-round-pill nl-debate-round-pill-final">
            <Sparkles size={11} /> Final
          </span>
          Final Consensus &amp; Summary
          {contentMode === "streaming" && (
            <span className="nl-debate-round-status">
              <Loader2 size={11} className="nl-spin" /> Streaming…
            </span>
          )}
        </span>
        <ChevronDown
          size={18}
          className={`nl-debate-chev ${isOpen ? "is-open" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="nl-debate-round-body">
          {contentMode === "pending" ? (
            <p className="nl-debate-round-pending">
              Waiting for Round 3 to finish…
            </p>
          ) : (
            <div className="nl-debate-consensus-card">
              <div className="nl-debate-consensus-head">
                <div className="nl-debate-consensus-author">
                  <span
                    className="nl-debate-role-logo"
                    style={{
                      background: "#d977571a",
                      borderColor: "#d9775740",
                    }}
                  >
                    <img
                      src="https://cdn.simpleicons.org/claude/d97757"
                      alt="Claude"
                    />
                  </span>
                  <div>
                    <strong style={{ color: "#d97757" }}>Claude</strong>
                    <span className="nl-debate-arg-role">Final Consensus Summary</span>
                  </div>
                </div>
                <span className="nl-debate-strength">
                  Position:{" "}
                  <strong className="nl-strong-strong">
                    {FINAL_CONSENSUS.strength}
                  </strong>
                  <span
                    style={{
                      display: "block",
                      fontSize: 10,
                      fontWeight: 500,
                      color: "var(--nl-text-muted)",
                    }}
                  >
                    but fact-dependent
                  </span>
                </span>
              </div>
              <div ref={contentRef} className="nl-debate-consensus-export">
                <ul className="nl-debate-consensus-bullets">
                  {displayBullets.map((b, i, arr) => (
                    <li key={b}>
                      <CheckCircle2 size={13} />
                      <span>
                        {b}
                        {contentMode === "streaming" &&
                          isStreaming &&
                          i === arr.length - 1 && <StreamCursor active />}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Round content mode: preview (idle), pending, streaming, complete */
function roundContentMode(roundNum, isRunning, activeRound, unlockedRound) {
  if (isRunning) {
    if (roundNum > unlockedRound) return "pending";
    if (roundNum === activeRound) return "streaming";
    if (roundNum < activeRound) return "complete";
    return "pending";
  }
  return "preview";
}

function finalContentMode(isRunning, activeRound, unlockedRound) {
  if (isRunning) {
    if (unlockedRound < 4) return "pending";
    if (activeRound === 4) return "streaming";
    if (activeRound === 0 && unlockedRound >= 4) return "complete";
    return "pending";
  }
  return "preview";
}

export default function NewLandingDebateSection() {
  const navigate = useNavigate();
  const { openPreviewEditor } = useLandingPreviewEditor();
  const consensusContentRef = useRef(null);
  const [expandedRounds, setExpandedRounds] = useState({ 1: true });
  const [runGeneration, setRunGeneration] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  /** 0 idle, 1–3 debate rounds, 4 final consensus */
  const [activeRound, setActiveRound] = useState(0);
  /** Highest round unlocked this run (1–4) */
  const [unlockedRound, setUnlockedRound] = useState(0);

  const [selectedRounds, setSelectedRounds] = useState(3);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleRound = (key) => {
    setExpandedRounds((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const runDebate = () => {
    if (selectedRounds > 3) {
      setShowUnlockModal(true);
      return;
    }
    setRunGeneration((g) => g + 1);
    setIsRunning(true);
    setActiveRound(1);
    setUnlockedRound(1);
    setExpandedRounds({ 1: true });
  };

  const advanceRound = useCallback(() => {
    setActiveRound((current) => {
      if (current < 3) {
        const next = current + 1;
        setUnlockedRound(next);
        setExpandedRounds((s) => ({ ...s, [next]: true }));
        return next;
      }
      if (current === 3) {
        setUnlockedRound(4);
        setExpandedRounds((s) => ({ ...s, 0: true }));
        return 4;
      }
      setIsRunning(false);
      return 0;
    });
  }, []);

  const handleFinalComplete = useCallback(() => {
    setIsRunning(false);
    setActiveRound(0);
  }, []);

  const handlePanelEditExport = useCallback(() => {
    const fallback = finalConsensusEditorPayload();
    openPreviewEditor(
      fallback.title,
      consensusContentRef.current?.innerHTML || fallback.html,
    );
  }, [openPreviewEditor]);

  return (
    <section className="debate-section" id="debate-preview">
      <div className="debate-inner">
        <p className="section-label">Debate Mode</p>
        <h2 className="section-title nl-debate-title">
          Stress-Test Your Argument Before Opposing Counsel Does.
        </h2>
        <p className="section-sub">
          LawFusion assigns each AI a role — Advocate, Critic, and Strategist —
          then runs a structured multi-round debate to expose weaknesses, sharpen
          your position, and deliver a final consensus.
        </p>

        <ProductPreviewShell>
          <div className="nl-debate-app">
            <div className="nl-debate-topbar">
              <div className="nl-debate-tab-pill">
                <span className="nl-tp-dot" />
                Debate Mode · Sample Preview
              </div>
              <div className="nl-debate-topbar-right">
                <span className="nl-debate-rounds-meta">
                  {isRunning ? (
                    <>
                      <Loader2 size={12} className="nl-spin" />
                      {activeRound <= 3
                        ? `Round ${activeRound} of ${selectedRounds}…`
                        : "Final summary…"}
                    </>
                  ) : (
                    <div className="nl-debate-dropdown-container" ref={dropdownRef}>
                      <button
                        type="button"
                        className="nl-debate-dropdown-trigger"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                      >
                        <Sparkles size={12} />
                        <span>{selectedRounds}-round debate</span>
                        <ChevronDown size={12} className={`nl-dropdown-arrow ${dropdownOpen ? "is-open" : ""}`} />
                      </button>
                      {dropdownOpen && (
                        <div className="nl-debate-dropdown-menu">
                          <button
                            type="button"
                            className={`nl-debate-dropdown-item${selectedRounds === 3 ? " is-active" : ""}`}
                            onClick={() => {
                              setSelectedRounds(3);
                              setDropdownOpen(false);
                            }}
                          >
                            3-round debate <span className="nl-dropdown-badge-free">Free</span>
                          </button>
                          <button
                            type="button"
                            className={`nl-debate-dropdown-item${selectedRounds === 5 ? " is-active" : ""}`}
                            onClick={() => {
                              setSelectedRounds(5);
                              setDropdownOpen(false);
                            }}
                          >
                            5-round debate <span className="nl-dropdown-badge-premium">Trial</span>
                          </button>
                          <button
                            type="button"
                            className={`nl-debate-dropdown-item${selectedRounds === 8 ? " is-active" : ""}`}
                            onClick={() => {
                              setSelectedRounds(8);
                              setDropdownOpen(false);
                            }}
                          >
                            8-round debate <span className="nl-dropdown-badge-pro">Pro</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </span>
                <button
                  type="button"
                  className={`nl-debate-run-btn${isRunning ? " is-running" : ""}`}
                  onClick={runDebate}
                  disabled={isRunning}
                >
                  {isRunning ? (
                    <>
                      <Loader2 size={13} className="nl-spin" /> Running…
                    </>
                  ) : (
                    <>
                      Run debate <Play size={13} />
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="nl-debate-query">
              <label className="nl-research-label">Sample Debate Question</label>
              <div className="nl-research-textarea">{DEBATE_QUERY}</div>
            </div>

            <div className="nl-debate-roles">
              {DEBATE_ROLES.map((role) => (
                <div className="nl-debate-role-card" key={role.key}>
                  <div
                    className="nl-debate-role-accent"
                    style={{ background: role.accent }}
                  />
                  <div className="nl-debate-role-head">
                    <span
                      className="nl-debate-role-logo"
                      style={{
                        background: `${role.accent}1a`,
                        borderColor: `${role.accent}40`,
                      }}
                    >
                      <img src={roleLogo(role)} alt={role.model} />
                    </span>
                    <div className="nl-debate-role-meta">
                      <span
                        className="nl-debate-role-title"
                        style={{ color: role.accent }}
                      >
                        {role.title}
                      </span>
                      <span className="nl-debate-role-model">
                        Assigned: <strong>{role.model}</strong>
                      </span>
                    </div>
                  </div>
                  <p className="nl-debate-role-sub">{role.subtitle}</p>
                </div>
              ))}
            </div>

            <div className="nl-debate-rounds">
              {DEBATE_ROUNDS.map((round) => (
                <DebateRoundBlock
                  key={round.round}
                  round={round}
                  runGeneration={runGeneration}
                  contentMode={roundContentMode(
                    round.round,
                    isRunning,
                    activeRound,
                    unlockedRound,
                  )}
                  isOpen={
                    isRunning
                      ? round.round <= unlockedRound && !!expandedRounds[round.round]
                      : !!expandedRounds[round.round]
                  }
                  onToggle={() => toggleRound(round.round)}
                  onRoundComplete={advanceRound}
                />
              ))}

              <FinalConsensusBlock
                runGeneration={runGeneration}
                contentMode={finalContentMode(
                  isRunning,
                  activeRound,
                  unlockedRound,
                )}
                isOpen={
                  isRunning
                    ? unlockedRound >= 4 && !!expandedRounds[0]
                    : !!expandedRounds[0]
                }
                onToggle={() => toggleRound(0)}
                onComplete={handleFinalComplete}
                contentRef={consensusContentRef}
              />
            </div>

            <div className="nl-debate-compare">
              <div className="nl-debate-compare-intro">
                <div className="nl-debate-compare-heading">
                  Re-run Your Revised Brief and See What Changed.
                </div>
                <div className="nl-debate-compare-sub">
                  After you revise, LawFusion compares the original debate against
                  the re-run — showing what improved, what still remains weak, and
                  whether your position actually got stronger.
                </div>
                <button
                  type="button"
                  className="nl-debate-rerun"
                  onClick={() => navigate("/debate")}
                >
                  <RotateCcw size={13} aria-hidden="true" />
                  Re-run with Revised Document →
                </button>
              </div>
              <div className="nl-debate-compare-card">
                <div className="nl-debate-compare-card-head">
                  <div className="nl-debate-compare-card-title">
                    <Clock size={13} aria-hidden="true" />
                    Comparison Dashboard
                    <span className="nl-debate-compare-vs">
                      Original Brief → Revised Brief
                    </span>
                  </div>
                  <span className="nl-debate-compare-badge">STRONGER</span>
                </div>
                <div className="nl-debate-compare-rows">
                  <div className="nl-debate-compare-row">
                    <span className="nl-debate-compare-lbl improved">
                      What Improved
                    </span>
                    <span className="nl-debate-compare-val">
                      {COMPARISON_DASHBOARD.improved}
                    </span>
                  </div>
                  <div className="nl-debate-compare-row">
                    <span className="nl-debate-compare-lbl weak">
                      What Remains Weak
                    </span>
                    <span className="nl-debate-compare-val">
                      {COMPARISON_DASHBOARD.weak}
                    </span>
                  </div>
                  <div className="nl-debate-compare-row">
                    <span className="nl-debate-compare-lbl risk">
                      Remaining Risk
                    </span>
                    <span className="nl-debate-compare-val">
                      {COMPARISON_DASHBOARD.risk}
                    </span>
                  </div>
                </div>
                <div className="nl-debate-compare-foot">
                  Full comparison dashboard preview →
                </div>
              </div>
            </div>

            <PreviewPanelActions page="debate" onEditExport={handlePanelEditExport} />
          </div>
        </ProductPreviewShell>

        <p className="preview-disclaimer">
          <span>
            <Info size={13} aria-hidden="true" />
            Preview only — full outputs include complete debate rounds, all three
            roles, Final Consensus, Executive Summary, and Comparison Dashboard.
          </span>
        </p>

        <SectionCta
          onClick={() => navigate("/debate")}
          sub="No login required · Free debate available"
        >
          Try Debate Mode Free
        </SectionCta>
      </div>

      {showUnlockModal && (
        <div className="nl-modal-overlay">
          <div className="nl-modal-card">
            <button
              type="button"
              className="nl-modal-close"
              onClick={() => setShowUnlockModal(false)}
            >
              &times;
            </button>
            <div className="nl-modal-icon">
              <Sparkles size={28} />
            </div>
            <h3>Unlock {selectedRounds}-Round Debates</h3>
            <p>
              Deep adversarial debates with {selectedRounds} full rounds of AI analysis,
              cross-examination, and comprehensive strategic planning are available
              in our trial and premium plans.
            </p>
            <div className="nl-modal-features">
              <div className="nl-modal-feature-item">
                <CheckCircle2 size={16} /> Multi-model adversarial debate loop
              </div>
              <div className="nl-modal-feature-item">
                <CheckCircle2 size={16} /> Expose hidden litigation risks & vulnerabilities
              </div>
              <div className="nl-modal-feature-item">
                <CheckCircle2 size={16} /> Export citation-backed case strategies
              </div>
            </div>
            <div className="nl-modal-actions">
              <button
                type="button"
                className="nl-modal-btn-primary"
                onClick={() => navigate("/pricing")}
              >
                Start Free Trial
              </button>
              <button
                type="button"
                className="nl-modal-btn-secondary"
                onClick={() => {
                  setSelectedRounds(3);
                  setShowUnlockModal(false);
                }}
              >
                Continue with 3-round Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
